// Supabase Auth + myr_profiles role (admin, entrepreneur, business_owner, partner, agent)
import { supabase } from '../lib/supabase';

const PROFILES_TABLE = 'myr_profiles';

/**
 * Sign in with email and password. Returns { ok, error, profile }.
 * Caller should check profile.role === 'admin' for admin access.
 */
export async function signIn(email, password) {
  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
  if (authError) return { ok: false, error: authError.message, profile: null };
  const profile = await getProfile(authData.user.id);
  return { ok: true, error: null, profile };
}

/**
 * Sign out (clears Supabase session).
 */
export async function signOut() {
  await supabase.auth.signOut();
}

/**
 * Get current session (user + access token).
 */
export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get current user.
 */
export async function getUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get profile for a user id (or current user). Creates profile with default role if missing.
 */
export async function getProfile(userId) {
  const id = userId || (await getUser())?.id;
  if (!id) return null;

  const { data, error } = await supabase.from(PROFILES_TABLE).select('id, role, full_name').eq('id', id).maybeSingle();
  if (error) return null;
  if (data) return data;

  // First time: create profile with default role
  const { data: inserted, error: insertErr } = await supabase
    .from(PROFILES_TABLE)
    .insert({ id, role: 'entrepreneur' })
    .select('id, role, full_name')
    .single();
  if (insertErr) return null;
  return inserted;
}

/**
 * Check if current user is authenticated and has admin role.
 */
export async function isAdmin() {
  const session = await getSession();
  if (!session?.user) return false;
  const profile = await getProfile(session.user.id);
  return profile?.role === 'admin';
}

/**
 * Subscribe to auth state changes (e.g. for React context).
 */
export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange(callback);
}

// Legacy API for drop-in compatibility where needed (async)
export const authService = {
  async login(email, password) {
    const result = await signIn(email, password);
    return result.ok && result.profile?.role === 'admin';
  },
  logout: signOut,
  async isAuthenticated() {
    const session = await getSession();
    return !!session;
  },
  async isAdmin() {
    return isAdmin();
  },
  getSession,
  getProfile,
  getUser,
};
